/**
 * Generated by orval v8.9.1 🍺
 * Do not edit manually.
 * Praetoid Dashboard API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult
} from '@tanstack/react-query';

import { customInstance } from '.././instance';
import type { ErrorType } from '.././instance';
import type { ApiKey, ApiKeyCreateRequest, ApiTokenListResponse, PraetoidError } from '../models';

/**
 * Returns all API keys for the specified environments
 * @summary List API keys
 */
export const listApiKeys = () => {
	return customInstance<ApiTokenListResponse>({ url: `/keys`, method: 'GET' });
};

export const getListApiKeysQueryKey = () => {
	return ['keys'] as const;
};

export const getListApiKeysQueryOptions = <
	TData = Awaited<ReturnType<typeof listApiKeys>>,
	TError = ErrorType<PraetoidError>
>(options?: {
	query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>>;
}) => {
	const { query: queryOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListApiKeysQueryKey();

	const queryFn: QueryFunction<Awaited<ReturnType<typeof listApiKeys>>> = () => listApiKeys();

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listApiKeys>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListApiKeysQueryResult = NonNullable<Awaited<ReturnType<typeof listApiKeys>>>;
export type ListApiKeysQueryError = ErrorType<PraetoidError>;

export function useListApiKeys<
	TData = Awaited<ReturnType<typeof listApiKeys>>,
	TError = ErrorType<PraetoidError>
>(
	options: {
		query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listApiKeys>>,
					TError,
					Awaited<ReturnType<typeof listApiKeys>>
				>,
				'initialData'
			>;
	},
	queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListApiKeys<
	TData = Awaited<ReturnType<typeof listApiKeys>>,
	TError = ErrorType<PraetoidError>
>(
	options?: {
		query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listApiKeys>>,
					TError,
					Awaited<ReturnType<typeof listApiKeys>>
				>,
				'initialData'
			>;
	},
	queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListApiKeys<
	TData = Awaited<ReturnType<typeof listApiKeys>>,
	TError = ErrorType<PraetoidError>
>(
	options?: {
		query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>>;
	},
	queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List API keys
 */

export function useListApiKeys<
	TData = Awaited<ReturnType<typeof listApiKeys>>,
	TError = ErrorType<PraetoidError>
>(
	options?: {
		query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>>;
	},
	queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
	const queryOptions = getListApiKeysQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
		queryKey: DataTag<QueryKey, TData, TError>;
	};

	return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new API key for the current environment. The raw key is only shown once.
 * @summary Create a new API key
 */
export const createApiKey = (apiKeyCreateRequest: ApiKeyCreateRequest) => {
	return customInstance<ApiKey>({
		url: `/keys`,
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		data: apiKeyCreateRequest
	});
};

export const getCreateApiKeyMutationOptions = <
	TError = ErrorType<PraetoidError>,
	TContext = unknown
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createApiKey>>,
		TError,
		{ data: ApiKeyCreateRequest },
		TContext
	>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createApiKey>>,
	TError,
	{ data: ApiKeyCreateRequest },
	TContext
> => {
	const mutationKey = ['createApiKey'];
	const { mutation: mutationOptions } = options
		? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey } };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createApiKey>>,
		{ data: ApiKeyCreateRequest }
	> = (props) => {
		const { data } = props ?? {};

		return createApiKey(data);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateApiKeyMutationResult = NonNullable<Awaited<ReturnType<typeof createApiKey>>>;
export type CreateApiKeyMutationBody = ApiKeyCreateRequest;
export type CreateApiKeyMutationError = ErrorType<PraetoidError>;

/**
 * @summary Create a new API key
 */
export const useCreateApiKey = <TError = ErrorType<PraetoidError>, TContext = unknown>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createApiKey>>,
			TError,
			{ data: ApiKeyCreateRequest },
			TContext
		>;
	},
	queryClient?: QueryClient
): UseMutationResult<
	Awaited<ReturnType<typeof createApiKey>>,
	TError,
	{ data: ApiKeyCreateRequest },
	TContext
> => {
	return useMutation(getCreateApiKeyMutationOptions(options), queryClient);
};
/**
 * Sets an API key to inactive (soft delete)
 * @summary Revoke an API key
 */
export const revokeApiKey = (id: string) => {
	return customInstance<void>({ url: `/keys/${id}`, method: 'DELETE' });
};

export const getRevokeApiKeyMutationOptions = <
	TError = ErrorType<PraetoidError>,
	TContext = unknown
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof revokeApiKey>>,
		TError,
		{ id: string },
		TContext
	>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof revokeApiKey>>,
	TError,
	{ id: string },
	TContext
> => {
	const mutationKey = ['revokeApiKey'];
	const { mutation: mutationOptions } = options
		? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey } };

	const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeApiKey>>, { id: string }> = (
		props
	) => {
		const { id } = props ?? {};

		return revokeApiKey(id);
	};

	return { mutationFn, ...mutationOptions };
};

export type RevokeApiKeyMutationResult = NonNullable<Awaited<ReturnType<typeof revokeApiKey>>>;

export type RevokeApiKeyMutationError = ErrorType<PraetoidError>;

/**
 * @summary Revoke an API key
 */
export const useRevokeApiKey = <TError = ErrorType<PraetoidError>, TContext = unknown>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof revokeApiKey>>,
			TError,
			{ id: string },
			TContext
		>;
	},
	queryClient?: QueryClient
): UseMutationResult<
	Awaited<ReturnType<typeof revokeApiKey>>,
	TError,
	{ id: string },
	TContext
> => {
	return useMutation(getRevokeApiKeyMutationOptions(options), queryClient);
};
